import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
 
  Select,
  MenuItem,
  
  FormControl,
  Box,
  Stack,
  Chip,
 
  Tooltip,
 
  Paper,
  Avatar,
 
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { BlockMath, InlineMath } from "react-katex";
import { MathfieldElement } from "mathlive";
import "katex/dist/katex.min.css";
import "mathlive";
import "../css/mathlive.core.css";
import { API_URL } from "../config";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import FunctionsIcon from "@mui/icons-material/Functions";
import TextFieldsIcon from "@mui/icons-material/TextFields";

if (!customElements.get("math-field")) {
  customElements.define("math-field", MathfieldElement);
}

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7", dark: "#92680C" },
    error: { main: "#DC2626", light: "#FEE2E2" },
    success: { main: "#059669", light: "#D1FAE5" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 860, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.30)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.40)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const ModeToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  background: alpha(theme.palette.primary.main, 0.06),
  borderRadius: 10,
  padding: 3,
  gap: 2,
}));

const ModeBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  minWidth: 80,
  padding: "6px 16px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  textTransform: "none",
  boxShadow: "none",
  transition: "all 0.18s ease",
  ...(active
    ? {
        background: "#fff",
        color: theme.palette.primary.main,
        boxShadow: "0 1px 6px rgba(0,0,0,0.10)",
        "&:hover": {
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        },
      }
    : {
        background: "transparent",
        color: theme.palette.text.disabled,
        "&:hover": {
          background: alpha("#fff", 0.5),
          color: theme.palette.text.secondary,
        },
      }),
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  fontFamily: "'Sora', sans-serif",
  fontSize: 14,
  borderRadius: 10,
  background: alpha(theme.palette.background.default, 0.8),
  "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#000", 0.1) },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 1.5,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
  },
}));

const AnswerCheckbox = styled(Box, {
  shouldForwardProp: (p) => p !== "checked",
})(({ theme, checked }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 16px",
  borderRadius: 10,
  cursor: "pointer",
  border: `1.5px solid ${
    checked ? alpha(theme.palette.success.main, 0.5) : alpha("#000", 0.08)
  }`,
  background: checked
    ? alpha(theme.palette.success.main, 0.05)
    : alpha(theme.palette.background.default, 0.5),
  transition: "all 0.2s ease",
  userSelect: "none",
  "&:hover": {
    borderColor: checked
      ? theme.palette.success.main
      : alpha(theme.palette.primary.main, 0.3),
    background: checked
      ? alpha(theme.palette.success.main, 0.08)
      : alpha(theme.palette.primary.main, 0.03),
  },
}));

const OptionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "isanswer" && p !== "itemdelay",
})(({ theme, isanswer, itemdelay }) => ({
  borderRadius: 14,
  border: `1.5px solid ${
    isanswer ? alpha(theme.palette.success.main, 0.4) : alpha("#000", 0.07)
  }`,
  boxShadow: isanswer
    ? `0 2px 12px ${alpha(theme.palette.success.main, 0.12)}`
    : "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  padding: "20px 24px",
  background: isanswer ? alpha(theme.palette.success.main, 0.025) : "#fff",
  marginBottom: 14,
  animation: `${fadeUp} 0.45s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: isanswer
      ? `0 4px 20px ${alpha(theme.palette.success.main, 0.18)}`
      : "0 4px 16px rgba(37,99,235,0.07), 0 8px 28px rgba(0,0,0,0.06)",
    transform: "translateY(-1px)",
  },
}));

const LetterBadge = styled(Avatar, {
  shouldForwardProp: (p) => p !== "isanswer",
})(({ theme, isanswer }) => ({
  width: 40,
  height: 40,
  borderRadius: 11,
  fontSize: 16,
  fontWeight: 800,
  fontFamily: "'DM Serif Display', Georgia, serif",
  flexShrink: 0,
  background: isanswer
    ? alpha(theme.palette.success.main, 0.15)
    : alpha(theme.palette.primary.main, 0.1),
  color: isanswer ? theme.palette.success.main : theme.palette.primary.main,
  border: `1.5px solid ${
    isanswer
      ? alpha(theme.palette.success.main, 0.3)
      : alpha(theme.palette.primary.main, 0.2)
  }`,
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: {
      bg: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.main,
      hbg: theme.palette.error.main,
    },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    transition: "all 0.18s ease",
    fontFamily: "'Sora', sans-serif",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const Spinner = styled(Box)(() => ({
  width: 18,
  height: 18,
  border: "2px solid rgba(37,99,235,0.15)",
  borderTop: "2px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
  display: "inline-block",
  flexShrink: 0,
}));

const LETTER_COLORS = {
  A: { bg: alpha("#2563EB", 0.1), color: "#2563EB" },
  B: { bg: alpha("#C4922A", 0.12), color: "#C4922A" },
  C: { bg: alpha("#7C3AED", 0.1), color: "#7C3AED" },
  D: { bg: alpha("#059669", 0.1), color: "#059669" },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminExamOptionPage() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const imageInputRef = useRef(null);

  const [options, setOptions] = useState([]);
  const [optionLetter, setOptionLetter] = useState("");
  const [optionText, setOptionText] = useState("");
  const [mode, setMode] = useState("text");
  const [isAnswer, setIsAnswer] = useState(false);
  const [imageUri, setImageUri] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchOptions = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/admin/exam/options/${questionId}/`)
      .then((res) => res.json())
      .then((data) => {
        setOptions(data);
        setLoading(false);
      })
      .catch(() => {
        alert("Failed to fetch options.");
        setLoading(false);
      });
  }, [questionId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const resetForm = () => {
    setOptionLetter("");
    setOptionText("");
    setIsAnswer(false);
    setImageUri(null);
    setImagePreview(null);
    setEditMode(false);
    setEditId(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removeBackgroundWithRemoveBg = async (file) => {
    const formData = new FormData();
    formData.append("image_file", file);
    formData.append("size", "auto");
    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: formData,
    });
    if (!res.ok) throw new Error("Background removal failed");
    return await res.blob();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsRemovingBg(true);
    try {
      const cleanedBlob = await removeBackgroundWithRemoveBg(file);
      const cleanedFile = new File([cleanedBlob], "cleaned.png", {
        type: "image/png",
      });
      setImageUri(cleanedFile);
      setImagePreview(URL.createObjectURL(cleanedFile));
    } catch {
      alert("Background removal failed – using original image.");
      setImageUri(file);
      setImagePreview(URL.createObjectURL(file));
    } finally {
      setIsRemovingBg(false);
    }
  };

  const handleSubmit = () => {
    if (!optionLetter || !optionText.trim()) {
      alert("Option Letter and Text are required.");
      return;
    }
    const formData = new FormData();
    formData.append("optionLetter", optionLetter);
    formData.append("text", optionText);
    formData.append("isAnswer", isAnswer);
    if (imageUri) formData.append("Eoption_img", imageUri);

    const url = editMode
      ? `${API_URL}/admin/exam/option/${editId}/`
      : `${API_URL}/admin/exam/options/${questionId}/`;

    fetch(url, { method: editMode ? "PUT" : "POST", body: formData })
      .then(() => {
        fetchOptions();
        resetForm();
      })
      .catch(() => alert("Error submitting option."));
  };

  const startEdit = (option) => {
    setEditMode(true);
    setEditId(option.id);
    setOptionLetter(option.option_letter);
    setIsAnswer(option.is_answer);
    setOptionText(option.text || "");
    setImagePreview(option.Eoption_img || null);
    setImageUri(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteOption = (optionId) => {
    if (!window.confirm("Delete this option permanently?")) return;
    fetch(`${API_URL}/admin/exam/option/${optionId}/`, { method: "DELETE" })
      .then(() => setOptions((prev) => prev.filter((o) => o.id !== optionId)))
      .catch(() => alert("Failed to delete option."));
  };

  const renderLatex = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  const correctCount = options.filter((o) => o.is_answer).length;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                {editMode ? "Edit Option" : "Add New Option"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Question ID:{" "}
                <strong style={{ color: "#4B5563" }}>{questionId}</strong>
              </Typography>
            </Box>
            <Chip
              label={editMode ? "Editing" : "New"}
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: editMode
                  ? alpha("#C4922A", 0.12)
                  : alpha("#2563EB", 0.1),
                color: editMode ? "#C4922A" : "#2563EB",
                border: `1px solid ${
                  editMode ? alpha("#C4922A", 0.25) : alpha("#2563EB", 0.2)
                }`,
              }}
            />
          </Box>

          {/* ── Form Card ── */}
          <SectionCard delay={50}>
            <CardLabel>Option Details</CardLabel>

            {/* Option Letter */}
            <Box sx={{ mb: 2.5, maxWidth: 220 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 0.8,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Option Letter
              </Typography>
              <FormControl fullWidth size="small">
                <StyledSelect
                  value={optionLetter}
                  displayEmpty
                  onChange={(e) => setOptionLetter(e.target.value)}
                  renderValue={(val) =>
                    val ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "6px",
                            background: LETTER_COLORS[val]?.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 800,
                              color: LETTER_COLORS[val]?.color,
                              fontFamily: "'DM Serif Display', serif",
                            }}
                          >
                            {val}
                          </Typography>
                        </Box>
                        Option {val}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: "text.disabled",
                          fontFamily: "'Sora', sans-serif",
                          fontSize: 13,
                        }}
                      >
                        Select…
                      </Typography>
                    )
                  }
                >
                  {["A", "B", "C", "D"].map((l) => (
                    <MenuItem
                      key={l}
                      value={l}
                      sx={{
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 600,
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "7px",
                          background: LETTER_COLORS[l]?.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: LETTER_COLORS[l]?.color,
                            fontFamily: "'DM Serif Display', serif",
                          }}
                        >
                          {l}
                        </Typography>
                      </Box>
                      Option {l}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Box>

            {/* Option Text + Mode Toggle */}
            <Box sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Option Text
                </Typography>
                <ModeToggle>
                  <ModeBtn
                    active={mode === "text" ? 1 : 0}
                    startIcon={
                      <TextFieldsIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={() => setMode("text")}
                  >
                    Text
                  </ModeBtn>
                  <ModeBtn
                    active={mode === "math" ? 1 : 0}
                    startIcon={
                      <FunctionsIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={() => setMode("math")}
                  >
                    Math
                  </ModeBtn>
                </ModeToggle>
              </Box>

              {mode === "text" ? (
                <Box
                  component="textarea"
                  placeholder="Enter option text (use $...$ for inline math, $$...$$ for block math)"
                  value={optionText}
                  onChange={(e) => setOptionText(e.target.value)}
                  sx={{
                    width: "100%",
                    minHeight: 110,
                    padding: "12px 14px",
                    fontSize: 14,
                    borderRadius: "10px",
                    fontFamily: "'Sora', sans-serif",
                    border: `1px solid ${alpha("#000", 0.1)}`,
                    background: alpha("#F0F2F8", 0.8),
                    color: "#0F1117",
                    resize: "vertical",
                    outline: "none",
                    lineHeight: 1.65,
                    transition: "all 0.2s ease",
                    boxSizing: "border-box",
                    "&:focus": {
                      borderColor: "#2563EB",
                      borderWidth: "1.5px",
                      background: "#fff",
                      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${alpha("#000", 0.1)}`,
                    borderRadius: "10px",
                    background: alpha("#F0F2F8", 0.8),
                    overflow: "hidden",
                    "&:focus-within": {
                      border: "1.5px solid #2563EB",
                      background: "#fff",
                      boxShadow: "0 0 0 3px rgba(37,99,235,0.08)",
                    },
                  }}
                >
                  <math-field
                    value={optionText}
                    onInput={(e) => setOptionText(e.target.getValue())}
                    style={{
                      width: "100%",
                      minHeight: 110,
                      border: "none",
                      padding: 12,
                      fontSize: 16,
                      background: "transparent",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Is Correct Answer */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 1,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Answer Status
              </Typography>
              <AnswerCheckbox
                checked={isAnswer}
                onClick={() => setIsAnswer(!isAnswer)}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "6px",
                    flexShrink: 0,
                    border: `2px solid ${
                      isAnswer ? "#059669" : alpha("#000", 0.2)
                    }`,
                    background: isAnswer ? "#059669" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.18s ease",
                  }}
                >
                  {isAnswer && (
                    <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: isAnswer ? "#059669" : "text.secondary",
                      fontFamily: "'Sora', sans-serif",
                      lineHeight: 1.2,
                    }}
                  >
                    {isAnswer
                      ? "Marked as correct answer"
                      : "Mark as correct answer"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.disabled",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  >
                    {isAnswer
                      ? "Students selecting this option will be marked correct"
                      : "Click to mark this option as the correct answer"}
                  </Typography>
                </Box>
                {isAnswer && (
                  <CheckCircleIcon
                    sx={{ ml: "auto", color: "#059669", fontSize: 20 }}
                  />
                )}
              </AnswerCheckbox>
            </Box>

            {/* Image Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: "text.disabled",
                  mb: 1,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Attach Image (optional)
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                <Button
                  component="label"
                  startIcon={<ImageIcon sx={{ fontSize: "15px !important" }} />}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    background: "#fff",
                    px: 2,
                    py: 0.9,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      background: alpha("#2563EB", 0.04),
                    },
                  }}
                >
                  Upload Image
                  <input
                    accept="image/*"
                    type="file"
                    hidden
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Background will be auto-removed
                </Typography>
                {isRemovingBg && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Spinner />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Removing background…
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {imagePreview && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      maxHeight: 180,
                      maxWidth: "100%",
                      borderRadius: 2,
                      display: "block",
                      border: `1px solid ${alpha("#000", 0.08)}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Submit Row */}
            <Stack direction="row" spacing={1.5}>
              <PrimaryBtn
                startIcon={<CheckIcon sx={{ fontSize: "16px !important" }} />}
                disabled={isRemovingBg}
                onClick={handleSubmit}
              >
                {editMode ? "Update Option" : "Add Option"}
              </PrimaryBtn>
              {editMode && (
                <Button
                  onClick={resetForm}
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    px: 2.5,
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          </SectionCard>

          {/* ── Options List ── */}
          <SectionCard delay={120}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CardLabel sx={{ mb: 0 }}>Existing Options</CardLabel>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${options.length} option${
                    options.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  sx={{
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 700,
                    fontSize: 11,
                    background: alpha("#2563EB", 0.08),
                    color: "#2563EB",
                    border: `1px solid ${alpha("#2563EB", 0.18)}`,
                  }}
                />
                {correctCount > 0 && (
                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          fontSize: "13px !important",
                          color: "#059669 !important",
                        }}
                      />
                    }
                    label={`${correctCount} correct`}
                    size="small"
                    sx={{
                      fontFamily: "'Sora', sans-serif",
                      fontWeight: 700,
                      fontSize: 11,
                      background: alpha("#059669", 0.08),
                      color: "#059669",
                      border: `1px solid ${alpha("#059669", 0.2)}`,
                    }}
                  />
                )}
              </Stack>
            </Box>

            {/* Loading */}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 5,
                  gap: 2,
                }}
              >
                <Spinner
                  sx={{
                    width: 32,
                    height: 32,
                    borderWidth: 3,
                    borderTopWidth: 3,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  Loading options…
                </Typography>
              </Box>
            ) : options.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <TextFieldsIcon
                    sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No options added yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora', sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to add options A, B, C, D
                </Typography>
              </Box>
            ) : (
              options.map((option, i) => (
                <OptionCard
                  key={option.id}
                  isanswer={option.is_answer ? 1 : 0}
                  itemdelay={i * 60}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    {/* Letter Badge */}
                    <LetterBadge
                      isanswer={option.is_answer ? 1 : 0}
                      sx={
                        option.is_answer
                          ? {}
                          : {
                              background:
                                LETTER_COLORS[option.option_letter]?.bg ||
                                alpha("#2563EB", 0.1),
                              color:
                                LETTER_COLORS[option.option_letter]?.color ||
                                "#2563EB",
                              border: `1.5px solid ${alpha(
                                LETTER_COLORS[option.option_letter]?.color ||
                                  "#2563EB",
                                0.25
                              )}`,
                            }
                      }
                    >
                      {option.option_letter}
                    </LetterBadge>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.8,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "text.primary",
                            fontFamily: "'Sora', sans-serif",
                          }}
                        >
                          Option {option.option_letter}
                        </Typography>
                        {option.is_answer && (
                          <Chip
                            icon={
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "13px !important",
                                  color: "#059669 !important",
                                }}
                              />
                            }
                            label="Correct Answer"
                            size="small"
                            sx={{
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: 700,
                              fontSize: 10,
                              background: alpha("#059669", 0.1),
                              color: "#059669",
                              border: `1px solid ${alpha("#059669", 0.25)}`,
                            }}
                          />
                        )}
                      </Box>

                      <Box sx={{ wordBreak: "break-word" }}>
                        {renderLatex(option.text)}
                      </Box>

                      {option.Eoption_img && (
                        <Box sx={{ mt: 2 }}>
                          <Box
                            component="img"
                            src={option.Eoption_img}
                            alt="Option"
                            sx={{
                              maxHeight: 160,
                              maxWidth: "100%",
                              borderRadius: 2,
                              display: "block",
                              border: `1px solid ${alpha("#000", 0.08)}`,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Stack
                      direction="row"
                      spacing={0.8}
                      sx={{ flexShrink: 0, pt: 0.2 }}
                    >
                      <Tooltip title="Edit option" arrow>
                        <ActionBtn
                          btncolor="edit"
                          onClick={() => startEdit(option)}
                        >
                          <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                          Edit
                        </ActionBtn>
                      </Tooltip>
                      <Tooltip title="Delete option" arrow>
                        <ActionBtn
                          btncolor="delete"
                          onClick={() => deleteOption(option.id)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                          Delete
                        </ActionBtn>
                      </Tooltip>
                    </Stack>
                  </Box>
                </OptionCard>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
